<template>
  <!-- Price guide ("cote") of the products sold by a listing (API products.py):
       finished sales in Japan per condition, the listing's one highlighted -->
  <section class="price-guide" aria-labelledby="price-guide-title">
    <div class="price-guide__heading">
      <h3 id="price-guide-title">Cote</h3>
      <button
        v-if="state.status === 'ready' || state.status === 'error'"
        type="button"
        class="link"
        :disabled="busy"
        title="Relire les ventes terminées et identifier le produit à nouveau"
        @click="compute(true)"
      >
        ↻ Actualiser la cote
      </button>
    </div>

    <p v-if="loading" class="note">Chargement de la cote…</p>
    <p v-else-if="state.status === 'pending'" class="note pending">
      <span class="spinner" aria-hidden="true"></span>
      Calcul en cours : identification du produit et lecture des ventes
      terminées (Yahoo, Mercari)…
    </p>
    <div v-else-if="state.status === 'none'" class="note">
      Pas encore de cote pour cette annonce.
      <button type="button" class="link" :disabled="busy" @click="compute()">
        Calculer la cote
      </button>
    </div>
    <p v-else-if="state.status === 'no_ai'" class="note">
      La cote a besoin de l’IA pour reconnaître le produit : la clé
      <code>OPENAI_API_KEY</code> n’est pas configurée sur le serveur.
      <button type="button" class="link" @click="correcting = true">
        Indiquer le produit
      </button>
    </p>
    <p v-else-if="state.status === 'unidentified'" class="note">
      Produit non reconnu dans cette annonce.
      <button type="button" class="link" @click="correcting = true">
        Indiquer le produit
      </button>
    </p>
    <p v-else-if="state.status === 'error'" class="note error">
      La cote n’a pas pu être calculée{{
        state.error ? ` (${state.error})` : ""
      }}.
    </p>

    <article
      v-for="product in state.products"
      :key="product.id"
      class="product"
    >
      <header>
        <div>
          <strong>{{ product.name }}</strong>
          <small
            >{{
              [
                product.platform,
                kindLabel(product.kind),
                product.quantity > 1 ? `× ${product.quantity}` : "",
              ]
                .filter(Boolean)
                .join(" · ")
            }}
            · {{ refreshedLabel(product) }}</small
          >
          <small v-if="countsLabel(product)" class="counts">{{
            countsLabel(product)
          }}</small>
        </div>
        <button type="button" class="link small" @click="correcting = true">
          Pas le bon produit ?
        </button>
      </header>

      <table v-if="hasPrices(product)">
        <thead>
          <tr>
            <th>État</th>
            <th>Japon · ventes terminées</th>
            <th><span class="sr-only">Ventes</span></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="condition in conditionsOf(product)" :key="condition">
            <tr :class="{ highlighted: condition === product.condition }">
              <td>
                {{ conditionLabel(condition) }}
                <small v-if="condition === product.condition">annonce</small>
              </td>
              <td>
                <strong>{{ yen(product.prices.jp[condition].median) }}</strong>
                <small>
                  ≈ {{ euro(product.prices.jp[condition].median_eur) }} · de
                  {{ yen(product.prices.jp[condition].p25) }} à
                  {{ yen(product.prices.jp[condition].p75) }}</small
                >
              </td>
              <td>
                <button
                  type="button"
                  class="link small"
                  :aria-expanded="isOpen(product, condition)"
                  @click="toggleSales(product, condition)"
                >
                  {{ salesLabel(product.prices.jp[condition]) }}
                </button>
              </td>
            </tr>
            <tr v-if="isOpen(product, condition)" class="sales-row">
              <td colspan="3">
                <p v-if="sales.loading" class="note">Chargement…</p>
                <ul v-else>
                  <li v-for="sale in sales.list" :key="sale.url">
                    <a :href="sale.url" target="_blank" rel="noopener">{{
                      sale.title
                    }}</a>
                    <span
                      >{{ yen(sale.price) }} · {{ sourceLabel(sale.source) }}
                      {{ sale.sold_at ? `· ${date(sale.sold_at)}` : "" }}</span
                    >
                  </li>
                </ul>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <p v-else class="note">
        Aucune vente terminée trouvée pour ce produit
        {{ product.refreshed_at ? "" : "(pas encore lues)" }}.
      </p>
      <p v-if="currentGap(product)" class="current">
        Prix actuel de l’annonce : <strong>{{ yen(currentPrice) }}</strong>
        <span :class="currentGap(product).className">{{
          currentGap(product).label
        }}</span>
      </p>
      <p v-if="Object.keys(product.refresh_errors || {}).length" class="note">
        Source indisponible lors de la dernière lecture :
        {{ Object.keys(product.refresh_errors).map(sourceLabel).join(", ") }}.
      </p>
    </article>

    <form v-if="correcting" class="correct" @submit.prevent="createProduct">
      <p>
        <strong>Quel produit vend cette annonce ?</strong> Cherche un produit
        déjà connu, ou décris-le.
      </p>
      <input
        v-model="search"
        type="search"
        placeholder="ex. Mario's Tennis, Virtual Boy…"
        @input="searchProducts"
      />
      <ul v-if="found.length" class="found">
        <li v-for="product in found" :key="product.id">
          <button type="button" @click="chooseProduct(product.id)">
            {{ product.name }}
            <small>{{ product.platform }}</small>
          </button>
        </li>
      </ul>
      <div class="new-product">
        <input v-model="draft.name" placeholder="Nom (ex. Mario's Tennis)" />
        <input v-model="draft.platform" placeholder="Plateforme" />
        <select v-model="draft.kind">
          <option value="game">Jeu</option>
          <option value="console">Console</option>
          <option value="accessory">Accessoire</option>
          <option value="other">Autre</option>
        </select>
      </div>
      <div class="actions">
        <button type="button" class="link" @click="correcting = false">
          Annuler
        </button>
        <button type="submit" class="primary" :disabled="!draft.name || busy">
          Utiliser ce produit
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import { formatDate } from "../../clock";
import { onBeforeUnmount, reactive, ref, watch } from "vue";
import axios from "axios";
import config from "@/config/config";
import { CONDITION_LABELS, formatYen } from "./CoteChip.vue";
import { notifyChanged } from "../../detailStack";
import { sourceLongLabel } from "../../sources";

const KINDS = {
  game: "jeu",
  console: "console",
  accessory: "accessoire",
  other: "",
};
const ORDER = ["loose", "boxed", "new", "any"];
const POLL_MS = 5000;
const POLL_MAX = 36; // 3 minutes

export default {
  name: "PriceGuide",
  props: {
    offerId: { type: String, required: true },
    // Current price of the listing, compared with the guide (JPY only)
    currentPrice: { type: [Number, String], default: null },
    currency: { type: String, default: "JPY" },
  },
  emits: ["changed"],
  setup(props, { emit }) {
    const state = reactive({ status: "none", products: [], error: null });
    const loading = ref(true);
    const busy = ref(false);
    const correcting = ref(false);
    const search = ref("");
    const found = ref([]);
    const draft = reactive({ name: "", platform: "", kind: "game" });
    const sales = reactive({ key: "", list: [], loading: false });
    let timer = null;
    let polls = 0;
    let request = 0;

    const load = async () => {
      const id = ++request;
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/offers/${props.offerId}/cote`,
        );
        if (id !== request) return;
        Object.assign(state, {
          status: data.status,
          products: data.products || [],
          error: data.error,
        });
        clearTimeout(timer);
        if (data.status === "pending" && polls++ < POLL_MAX)
          timer = setTimeout(load, POLL_MS);
        else if (data.status === "ready") {
          // The lists already on screen show the new guide in place
          notifyChanged("offer", {
            _key: props.offerId,
            cote: data.summary || null,
          });
          emit("changed");
        }
      } catch {
        if (id === request)
          Object.assign(state, { status: "none", products: [] });
      } finally {
        if (id === request) loading.value = false;
      }
    };
    const compute = async (force = false) => {
      busy.value = true;
      try {
        await axios.post(`${config.apiUrl}/offers/${props.offerId}/cote`, {
          force,
        });
        state.status = "pending";
        polls = 0;
        clearTimeout(timer);
        timer = setTimeout(load, POLL_MS);
      } finally {
        busy.value = false;
      }
    };
    const applyChoice = async (body) => {
      busy.value = true;
      try {
        const { data } = await axios.put(
          `${config.apiUrl}/offers/${props.offerId}/product`,
          body,
        );
        Object.assign(state, {
          status: data.status,
          products: data.products || [],
        });
        correcting.value = false;
        search.value = "";
        found.value = [];
        polls = 0;
        if (data.status === "pending") timer = setTimeout(load, POLL_MS);
        else load();
      } catch (error) {
        window.alert(
          error.response?.data?.error || "Le produit n’a pas pu être modifié.",
        );
      } finally {
        busy.value = false;
      }
    };
    const chooseProduct = (productId) => applyChoice({ product_id: productId });
    const createProduct = () => applyChoice({ ...draft });
    let searchTimer = null;
    const searchProducts = () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(async () => {
        if (search.value.trim().length < 2) {
          found.value = [];
          return;
        }
        const { data } = await axios.get(`${config.apiUrl}/products`, {
          params: { q: search.value },
        });
        found.value = Array.isArray(data) ? data : [];
      }, 300);
    };

    const toggleSales = async (product, condition) => {
      const key = `${product.id}:${condition}`;
      if (sales.key === key) {
        sales.key = "";
        return;
      }
      Object.assign(sales, { key, list: [], loading: true });
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/products/${product.id}/sales`,
          { params: { condition, market: "jp" } },
        );
        if (sales.key === key) sales.list = Array.isArray(data) ? data : [];
      } finally {
        sales.loading = false;
      }
    };
    const isOpen = (product, condition) =>
      sales.key === `${product.id}:${condition}`;

    const hasPrices = (product) =>
      Object.keys(product.prices?.jp || {}).length > 0;
    const conditionsOf = (product) =>
      ORDER.filter((condition) => product.prices?.jp?.[condition]);
    const conditionLabel = (condition) =>
      condition === "any"
        ? "Tous états"
        : CONDITION_LABELS[condition].charAt(0).toUpperCase() +
          CONDITION_LABELS[condition].slice(1);
    const kindLabel = (kind) => KINDS[kind] || "";
    const sourceLabel = sourceLongLabel;
    const euro = (value) =>
      value == null
        ? "—"
        : new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
          }).format(value);
    const date = (value) => formatDate(value);
    const salesLabel = (guide) =>
      `${guide.count} vente${guide.count > 1 ? "s" : ""}${
        guide.outliers
          ? ` (+${guide.outliers} écartée${guide.outliers > 1 ? "s" : ""})`
          : ""
      }`;
    const refreshedLabel = (product) =>
      product.refreshed_at
        ? `cote du ${date(product.refreshed_at)}`
        : "ventes pas encore lues";
    // Finished sales read per source at the last refresh (before sorting)
    const countsLabel = (product) => {
      const counts = Object.entries(product.refresh_counts || {});
      if (!counts.length) return "";
      return `Ventes lues : ${counts
        .map(([source, count]) => `${sourceLabel(source)} ${count}`)
        .join(" · ")}`;
    };
    const currentGap = (product) => {
      const price = Number(props.currentPrice);
      const guide =
        product.prices?.jp?.[product.condition] ||
        product.prices?.jp?.boxed ||
        product.prices?.jp?.any;
      if (!price || !guide?.median || props.currency !== "JPY") return null;
      const gap = Math.round(((price - guide.median) / guide.median) * 100);
      return {
        label:
          gap === 0
            ? "au prix de la cote"
            : `${gap > 0 ? "+" : "−"}${Math.abs(gap)} % par rapport à la cote`,
        className: gap <= -10 ? "below" : gap >= 10 ? "above" : "",
      };
    };

    watch(
      () => props.offerId,
      () => {
        loading.value = true;
        polls = 0;
        sales.key = "";
        correcting.value = false;
        load();
      },
      { immediate: true },
    );
    onBeforeUnmount(() => {
      clearTimeout(timer);
      clearTimeout(searchTimer);
    });

    return {
      busy,
      chooseProduct,
      compute,
      conditionLabel,
      conditionsOf,
      countsLabel,
      correcting,
      createProduct,
      currentGap,
      date,
      draft,
      euro,
      found,
      hasPrices,
      isOpen,
      kindLabel,
      loading,
      refreshedLabel,
      sales,
      salesLabel,
      search,
      searchProducts,
      sourceLabel,
      state,
      toggleSales,
      yen: formatYen,
    };
  },
};
</script>

<style scoped>
.price-guide {
  display: grid;
  gap: 12px;
}
.price-guide__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.price-guide h3 {
  margin: 0;
  color: #26324a;
  font-size: 13px;
}
.link {
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.link.small {
  font-size: 11px;
}
.link:disabled {
  opacity: 0.5;
}
.note {
  margin: 0;
  color: #6f7b8d;
  font-size: 12px;
  line-height: 1.5;
}
.note.error {
  color: #b2423d;
}
.note code {
  font-size: 11px;
}
.pending {
  display: flex;
  align-items: center;
  gap: 8px;
}
.spinner {
  width: 13px;
  height: 13px;
  flex: 0 0 13px;
  border: 2px solid #d5dce5;
  border-top-color: #168a64;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.product {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid #e1e6ed;
  border-radius: 10px;
  background: #fff;
}
.product header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.product header strong,
.product header small {
  display: block;
}
.product header strong {
  color: #26324a;
  font-size: 13px;
}
.product header small {
  margin-top: 2px;
  color: #7b8797;
  font-size: 11px;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
/* The global table styles of the app (src/assets/styles.css) do not apply here */
.price-guide table tr,
.price-guide table tr:nth-child(even),
.price-guide table tr:hover {
  background: transparent;
}
.price-guide table th,
.price-guide table td {
  border-bottom: 0;
}
.price-guide table th {
  background: none;
  padding: 0 6px 6px 0;
  color: #8a95a5;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
}
.price-guide table td {
  padding: 8px 6px 8px 0;
  border-top: 1px solid #edf0f4;
  vertical-align: top;
}
td:last-child {
  text-align: right;
  white-space: nowrap;
}
td strong {
  color: #26324a;
  font-size: 13px;
}
td small {
  display: block;
  margin-top: 2px;
  color: #7b8797;
  font-size: 11px;
}
.price-guide table tr.highlighted td {
  background: #f2faf6;
}
tr.highlighted td:first-child {
  box-shadow: inset 3px 0 0 #168a64;
  padding-left: 8px;
}
td:first-child small {
  display: inline;
  margin-left: 4px;
  color: #168a64;
  font-weight: 700;
}
.sales-row td {
  padding-top: 0;
  border-top: 0;
  text-align: left;
  white-space: normal;
}
.sales-row ul {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f7f9fb;
  list-style: none;
}
.sales-row li {
  display: grid;
  gap: 1px;
}
.sales-row a {
  overflow: hidden;
  color: #2e6098;
  font-size: 11px;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sales-row span {
  color: #7b8797;
  font-size: 11px;
}
.current {
  margin: 0;
  color: #5d6a7c;
  font-size: 12px;
}
.current span {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef1f5;
  font-weight: 700;
}
.current span.below {
  background: #e7f6ef;
  color: #137551;
}
.current span.above {
  background: #fdf0ef;
  color: #b2423d;
}
.correct {
  display: grid;
  gap: 8px;
  padding: 14px;
  border: 1px solid #e1e6ed;
  border-radius: 10px;
  background: #f9fbfc;
}
.correct p {
  margin: 0;
  color: #5d6a7c;
  font-size: 12px;
}
.correct input,
.correct select {
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #ccd5df;
  border-radius: 8px;
  font: inherit;
  font-size: 12px;
}
.found {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.found button {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #e1e6ed;
  border-radius: 7px;
  background: #fff;
  font: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}
.found small {
  margin-left: 6px;
  color: #7b8797;
}
.new-product {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 6px;
}
.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}
.primary {
  height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: #168a64;
  color: #fff;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.primary:disabled {
  opacity: 0.5;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
@media (max-width: 520px) {
  .new-product {
    grid-template-columns: 1fr;
  }
}
</style>
